import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import SectorDots      from '../../components/admin/SectorDots';
import MemberGenderField from '../../components/admin/MemberGenderField';
import SectorScoreCard from '../../components/dashboard/SectorScoreCard';
import { SECTORS, SECTOR_MAP } from '../../utils/sectors';
import {
  getMemberDetail, sendManualNudge, addMemberNote, deleteMemberNote, sendMemberPasswordReset,
  sendMemberMessage, forwardMemberCommunication, updateFounderPricing, setMemberSequenceDay, setDailyContentEnabled,
  setOnboardingContentEnabled, setRenewalContentEnabled,
  setMemberActive, deleteMember, mergeMember, getAdminMembers, setMemberTempPassword,
  setMemberCourseProgress, updateJoinedAt, updateMembership, updateMemberProfile,
  getMemberActivityPreferences, setMemberActivityPreference, setMemberReferrer,
  saveMentoringStrategy, setLeadMentor, setMentorAccess, setSalesMarketingAccess,
} from '../../services/admin.service';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { createPartner as createAffiliatePartner, listPartners as listAffiliatePartners } from '../../services/adminAffiliate.service';

const MEMBERSHIP_TYPE_LABELS = { paid: 'Paid', trial: 'Free trial', free_annual: 'Free 1-year membership', royalty: 'Royalty member (free)' };

const SEQUENCE_LABELS = { onboarding: 'Onboarding', daily: 'Daily', renewal: 'Renewal' };

/** "Label | URL" per line, matching the CSV/course-lesson convention used everywhere else in the app. */
const textToLinks = (text) => (text || '').split('\n').map(s => s.trim()).filter(Boolean).map(line => {
  const [maybeTitle, maybeUrl] = line.split('|').map(s => s.trim());
  return maybeUrl ? { title: maybeTitle, url: maybeUrl } : { title: '', url: maybeTitle };
});

const COMMUNICATION_LABELS = {
  password_reset:       'Password reset email',
  welcome:               'Welcome email',
  magic_link:            'Sign-in link',
  invite:                'Invite email',
  nudge:                 'Nudge',
  weekly_summary:        'Weekly Compass summary',
  quarterly_snapshot:    'Quarterly check-in snapshot',
  quarterly_reminder:    'Quarterly check-in reminder',
  quarterly_comparison:  'Quarterly comparison (filed, not emailed)',
  admin_message:         'Message from Team365',
  cancellation:          'Cancellation email',
};

const SEND_ACTIVITY_LABELS = {
  chair_yoga_am: 'Morning chair yoga', chair_yoga_pm: 'Evening chair yoga',
  strength_cardio: 'Strength & Cardio Dance Party', reiki: 'Reiki Reset',
  meditation: 'Mindful Minutes meditation', tai_chi: 'Tai-Chi-Inspired Walking',
};

const ACTIVITY_FREQUENCY_OPTIONS = [
  { value: 'daily',    label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekly',   label: 'Weekly' },
];

/**
 * Jenn, 2026-09-06: can admins start/stop a member's activity opt-ins from
 * their file when they ask, and does it require a note? Yes to both — every
 * change here requires typing a note, which becomes the real record of the
 * member's request (same standard as every other admin action taken on
 * someone's behalf).
 *
 * Jenn, 2026-09-17, real gap found live: this only ever flipped enabled
 * on/off, silently reusing whatever channel/time/frequency the member
 * already had (or the hardcoded default of email/08:00/daily if they never
 * had one) — the API underneath (`setMemberActivityPreference`) always
 * accepted real channel/sendTime/frequency values, nothing here ever sent
 * them. Now mirrors the exact real controls the member's own Settings page
 * offers (`ActivityPrefRow` in Settings.jsx) — same SMS/Email toggle, same
 * frequency choices, same time input — so Team365 can set up a member's
 * request exactly the way the member would themselves, not just approximate
 * it with defaults.
 *
 * send_time is wall-clock Atlantic everywhere in this app (see
 * memberActivityDispatch.service.js) — a member asking for a time in their
 * own zone (e.g. "9am ET") needs converting to Atlantic before typing it in
 * here; there's no per-member timezone field on this table to do it
 * automatically.
 */
function ActivityPreferencesSection({ memberId }) {
  const [data, setData] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    getMemberActivityPreferences(memberId).then(setData).catch(() => setError('Could not load activity preferences.'));
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (pref) => {
    setEditingKey(pref.activity_key);
    setDraft({
      enabled: pref.enabled,
      channel: pref.channel || 'email',
      sendTime: (pref.send_time || '08:00').slice(0, 5),
      frequency: pref.frequency || 'daily',
    });
    setNote('');
    setError(null);
  };

  const handleSave = async (activityKey) => {
    if (!note.trim()) { setError('A note explaining the request is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await setMemberActivityPreference(memberId, activityKey, { ...draft, note: note.trim() });
      setEditingKey(null);
      setDraft(null);
      setNote('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  const byKey = Object.fromEntries((data.preferences || []).map(p => [p.activity_key, p]));

  return (
    <section className="bg-white rounded-2xl shadow-sm p-5">
      <h3 className="text-sm font-semibold text-reset-brown mb-1">Send Exercises To Me</h3>
      <p className="text-xs text-gray-400 mb-3">send_time is Atlantic wall-clock — convert a member's requested time from their own zone before entering it.</p>
      {data.smsOptedOut && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3">
          This member texted STOP — all their SMS-channel activity reminders and nudges were automatically turned off. They can text START to opt back in, or re-enable individual reminders themselves in Settings.
        </p>
      )}
      <div className="space-y-2">
        {Object.entries(SEND_ACTIVITY_LABELS).map(([key, label]) => {
          const pref = byKey[key] || { activity_key: key, enabled: false, channel: 'email', send_time: '08:00', frequency: 'daily' };
          const isEditing = editingKey === key;
          return (
            <div key={key} className="border-b border-gray-50 last:border-0 pb-2 last:pb-0">
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="text-reset-brown">{label}</span>
                  {pref.enabled && !isEditing && (
                    <span className="text-xs text-gray-400 ml-2">{pref.channel} · {pref.frequency} · {pref.send_time?.slice(0, 5)} AT</span>
                  )}
                </div>
                {!isEditing && (
                  <button onClick={() => startEdit(pref)} className="text-xs font-medium text-gray-500 hover:text-reset-terracotta">
                    {pref.enabled ? 'Edit…' : 'Turn on…'}
                  </button>
                )}
              </div>

              {isEditing && (
                <div className="mt-2 pl-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button" role="switch" aria-checked={draft.enabled}
                      onClick={() => setDraft(d => ({ ...d, enabled: !d.enabled }))}
                      className={`relative w-10 h-6 rounded-full transition-colors focus:outline-none ${draft.enabled ? 'bg-reset-terracotta' : 'bg-gray-200'}`}
                    >
                      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${draft.enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                    <span className="text-xs text-gray-500">{draft.enabled ? 'On' : 'Off'}</span>
                  </div>

                  {draft.enabled && (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="inline-flex bg-reset-creamDark rounded-lg p-0.5">
                        {['sms', 'email'].map(c => (
                          <button key={c} type="button"
                            onClick={() => setDraft(d => ({ ...d, channel: c }))}
                            className={`text-xs font-semibold px-2.5 py-1 rounded-md ${draft.channel === c ? 'bg-white text-reset-brown shadow-sm' : 'text-gray-500'}`}>
                            {c === 'sms' ? 'SMS' : 'Email'}
                          </button>
                        ))}
                      </div>
                      <select
                        value={draft.frequency}
                        onChange={e => setDraft(d => ({ ...d, frequency: e.target.value }))}
                        className="text-xs font-semibold bg-reset-creamDark text-reset-brown rounded-lg px-2 py-1.5 border-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                      >
                        {ACTIVITY_FREQUENCY_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </select>
                      <input
                        type="time"
                        value={draft.sendTime}
                        onChange={e => setDraft(d => ({ ...d, sendTime: e.target.value }))}
                        className="text-xs font-semibold bg-reset-creamDark text-reset-brown rounded-lg px-2 py-1.5 border-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                      />
                      <span className="text-[11px] text-gray-400">Atlantic time</span>
                    </div>
                  )}

                  <input
                    value={note} onChange={e => setNote(e.target.value)}
                    placeholder="Note — why is this changing?"
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full max-w-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                  />
                  <div className="flex items-center gap-3">
                    <button onClick={() => handleSave(key)} disabled={saving} className="text-xs font-medium text-reset-terracotta disabled:opacity-50">
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => { setEditingKey(null); setDraft(null); }} className="text-xs text-gray-400">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </section>
  );
}

const formatMoney = (amount, currency) => {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

/** Human labels for the audit_log action codes worth showing as "activity" — anything not listed here still shows, just with its raw code. */
const ACTIVITY_LABELS = {
  LOGIN: 'Logged in', GOOGLE_LOGIN: 'Logged in (Google)', MAGIC_LINK_LOGIN: 'Logged in (sign-in link)',
  LOGOUT: 'Logged out', SIGNUP: 'Signed up', ONBOARDING_COMPLETE: 'Completed onboarding',
  VIEW_LESSON: 'Viewed a lesson', COMPLETE_LESSON: 'Completed a lesson',
  QUARTERLY_CHECKIN_SUBMITTED: 'Submitted quarterly check-in',
  CREATE_COMMUNITY_POST: 'Posted in My Hive', CREATE_COMMUNITY_COMMENT: 'Commented in My Hive',
  UPDATE_RESET_PATH: 'Updated Reset Path', UPDATE_NUDGE_PREFS: 'Updated nudge preferences',
  MILESTONE_REACHED: 'Reached a milestone', CREATE_GOAL: 'Set a goal', CREATE_MILESTONE: 'Set a milestone',
  PASSWORD_SET: 'Set a password', PASSWORD_RESET_COMPLETED: 'Reset password',
};

/**
 * Admin — individual member detail view.
 *
 * Shows:
 *  — Member info + onboarding phase + subscription status
 *  — Slip status alert
 *  — Latest sector score cards
 *  — 14-day check-in calendar (which days had check-ins)
 *  — Recent nudges sent
 *  — Course enrollment/progress
 *  — Staff notes (add/delete)
 *  — Manual nudge + admin-triggered password reset
 */
export default function MemberDetail() {
  const { id }                  = useParams();
  const navigate                = useNavigate();
  const [data, setData]         = useState(null);
  const [archiving, setArchiving] = useState(false);
  // Archive asks why they're leaving (Sales & Marketing To-Do 8, 2026-09-29) — optional.
  const [archivePanel, setArchivePanel] = useState(false);
  const [leaveReason, setLeaveReason] = useState('');
  const [leaveNote, setLeaveNote] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [merging, setMerging] = useState(false);
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [nudging, setNudging]   = useState(false);
  const [nudgeSent, setNudgeSent] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [newNote, setNewNote]   = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [composing, setComposing] = useState(false);
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody]     = useState('');
  const [msgLinksText, setMsgLinksText] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [expandedCommId, setExpandedCommId] = useState(null);
  const [previewComm, setPreviewComm] = useState(null);
  const [forwardTo, setForwardTo] = useState('');
  const [forwarding, setForwarding] = useState(false);
  const [forwardResult, setForwardResult] = useState(null); // { ok: true } | { ok: false, error }
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState({ name: '', phone: '', email: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [editingFounderPricing, setEditingFounderPricing] = useState(false);
  const [makingAffiliate, setMakingAffiliate] = useState(false);
  const [affiliateDraft, setAffiliateDraft] = useState({ partnerType: 'affiliate', referralCode: '', commissionRatePct: 10 });
  const [savingAffiliate, setSavingAffiliate] = useState(false);
  const [affiliateError, setAffiliateError] = useState(null);
  const [editingMembership, setEditingMembership] = useState(false);
  const [savingMembership, setSavingMembership] = useState(false);
  const [membershipSyncNote, setMembershipSyncNote] = useState(null);
  const [editingJoinedAt, setEditingJoinedAt] = useState(false);
  const [joinedAtDraft, setJoinedAtDraft] = useState('');
  const [savingJoinedAt, setSavingJoinedAt] = useState(false);
  const [editingSequenceType, setEditingSequenceType] = useState(null); // null | 'onboarding' | 'daily' | 'renewal'
  const [sequenceDayDraft, setSequenceDayDraft] = useState('');
  const [savingSequenceDay, setSavingSequenceDay] = useState(false);
  const [savingFounderPricing, setSavingFounderPricing] = useState(false);
  const [savingDailyEnabled, setSavingDailyEnabled] = useState(false);
  const [savingOnboardingEnabled, setSavingOnboardingEnabled] = useState(false);
  const [savingRenewalEnabled, setSavingRenewalEnabled] = useState(false);
  const commsRef = useRef(null);

  const openComposer = () => {
    setComposing(true);
    commsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await getMemberDetail(id);
      setData(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load member.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleNudge = async () => {
    if (!window.confirm('Send a slip detection nudge to this member now?')) return;
    setNudging(true);
    try {
      await sendManualNudge(id);
      setNudgeSent(true);
      setTimeout(() => setNudgeSent(false), 5000);
      load(); // refresh to show new nudge in history
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send nudge.');
    } finally {
      setNudging(false);
    }
  };

  const handleSendReset = async () => {
    if (!window.confirm('Send this member a password reset link now?')) return;
    setSendingReset(true);
    try {
      await sendMemberPasswordReset(id);
      setResetSent(true);
      setTimeout(() => setResetSent(false), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send reset email.');
    } finally {
      setSendingReset(false);
    }
  };

  const handleToggleActive = async () => {
    const willActivate = data?.member.is_active === false;
    if (!willActivate && !archivePanel) { setArchivePanel(true); setLeaveReason(''); setLeaveNote(''); return; }
    if (willActivate && !window.confirm(`Reactivate ${data.member.name || 'this member'}?`)) return;
    setArchiving(true);
    try {
      await setMemberActive(id, willActivate, willActivate ? {} : { leaveReason: leaveReason || undefined, leaveNote: leaveNote.trim() || undefined });
      setArchivePanel(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this member.');
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async () => {
    const name = data?.member.name || data?.member.email || 'this member';
    if (!window.confirm(`Permanently delete ${name}? This erases their entire history — check-ins, scores, goals, everything — and can't be undone.`)) return;
    if (!window.confirm(`Really sure? Type-to-confirm isn't required, but there's no undo for ${name}.`)) return;
    setDeleting(true);
    try {
      await deleteMember(id);
      navigate('/admin/members');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete this member.');
      setDeleting(false);
    }
  };

  const handleMerge = async (survivor) => {
    const loserName = data?.member.name || data?.member.email;
    if (!window.confirm(`Merge ${loserName} into ${survivor.name} (${survivor.email})? Every check-in, goal, score, and note from ${loserName} moves onto ${survivor.name}'s account, then ${loserName}'s duplicate record is deleted. This can't be undone.`)) return;
    setMerging(true);
    try {
      await mergeMember(id, survivor.id);
      navigate(`/admin/members/${survivor.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not merge these members.');
      setMerging(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      await addMemberNote(id, newNote.trim());
      setNewNote('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note? This can\'t be undone.')) return;
    await deleteMemberNote(noteId);
    load();
  };

  const handleSaveProfile = async () => {
    if (!profileDraft.name.trim()) { setError('Name cannot be blank.'); return; }
    const newEmail = profileDraft.email.trim().toLowerCase();
    if (!newEmail || !newEmail.includes('@')) { setError('Please enter a valid email address.'); return; }
    // Email doubles as the login — confirm since it's easy to fat-finger and
    // hard for the member to notice until they can't sign in.
    if (newEmail !== (member.email || '').toLowerCase()
        && !window.confirm(`Change this member's sign-in email to ${newEmail}? They'll sign in with this new address from now on.`)) {
      return;
    }
    setSavingProfile(true);
    try {
      await updateMemberProfile(id, { name: profileDraft.name.trim(), phone: profileDraft.phone.trim() || null, email: newEmail });
      setEditingProfile(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this member\'s profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleMakeAffiliate = async () => {
    if (affiliateDraft.partnerType === 'affiliate' && !affiliateDraft.referralCode.trim()) {
      setAffiliateError('A referral code is required for an affiliate.');
      return;
    }
    setSavingAffiliate(true);
    setAffiliateError(null);
    try {
      await createAffiliatePartner({
        name: member.name, email: member.email, memberId: member.id,
        partnerType: affiliateDraft.partnerType,
        referralCode: affiliateDraft.partnerType === 'affiliate' ? affiliateDraft.referralCode.trim() : null,
        commissionRatePct: Number(affiliateDraft.commissionRatePct),
      });
      setMakingAffiliate(false);
      setAffiliateDraft({ partnerType: 'affiliate', referralCode: '', commissionRatePct: 10 });
      load();
    } catch (err) {
      setAffiliateError(err.response?.data?.error || 'Could not create this partner.');
    } finally {
      setSavingAffiliate(false);
    }
  };

  const handleSaveFounderPricing = async (fields) => {
    setSavingFounderPricing(true);
    try {
      await updateFounderPricing(id, fields);
      setEditingFounderPricing(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save founder pricing.');
    } finally {
      setSavingFounderPricing(false);
    }
  };

  const handleSaveMembership = async (fields) => {
    setSavingMembership(true);
    setMembershipSyncNote(null);
    try {
      const result = await updateMembership(id, fields);
      setEditingMembership(false);
      if (result?.stripe_sync_note) setMembershipSyncNote(result.stripe_sync_note);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save membership details.');
    } finally {
      setSavingMembership(false);
    }
  };

  const handleSaveJoinedAt = async () => {
    setSavingJoinedAt(true);
    try {
      await updateJoinedAt(id, joinedAtDraft || null);
      setEditingJoinedAt(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the join date.');
    } finally {
      setSavingJoinedAt(false);
    }
  };

  const handleSaveSequenceDay = async (sequenceType) => {
    const day = Number(sequenceDayDraft);
    if (!Number.isInteger(day) || day < 1) { setError('Day must be a whole number, 1 or greater.'); return; }
    setSavingSequenceDay(true);
    try {
      await setMemberSequenceDay(id, sequenceType, day);
      setEditingSequenceType(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this.');
    } finally {
      setSavingSequenceDay(false);
    }
  };

  const handleToggleDailyContent = async (enabled) => {
    setSavingDailyEnabled(true);
    setError(null);
    try {
      await setDailyContentEnabled(id, enabled);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this.');
    } finally {
      setSavingDailyEnabled(false);
    }
  };

  const handleToggleOnboardingContent = async (enabled) => {
    setSavingOnboardingEnabled(true);
    setError(null);
    try {
      await setOnboardingContentEnabled(id, enabled);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this.');
    } finally {
      setSavingOnboardingEnabled(false);
    }
  };

  const handleToggleRenewalContent = async (enabled) => {
    setSavingRenewalEnabled(true);
    setError(null);
    try {
      await setRenewalContentEnabled(id, enabled);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this.');
    } finally {
      setSavingRenewalEnabled(false);
    }
  };

  const handleSendMessage = async () => {
    if (!msgSubject.trim() || !msgBody.trim()) return;
    setSendingMsg(true);
    try {
      await sendMemberMessage(id, { subject: msgSubject.trim(), body: msgBody.trim(), links: textToLinks(msgLinksText) });
      setMsgSubject('');
      setMsgBody('');
      setMsgLinksText('');
      setComposing(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send this message.');
    } finally {
      setSendingMsg(false);
    }
  };

  const handleForwardComm = async () => {
    if (!forwardTo.trim() || !previewComm) return;
    setForwarding(true);
    setForwardResult(null);
    try {
      await forwardMemberCommunication(id, previewComm.id, forwardTo.trim());
      setForwardResult({ ok: true });
      setForwardTo('');
    } catch (err) {
      setForwardResult({ ok: false, error: err.response?.data?.error || 'Could not forward this email.' });
    } finally {
      setForwarding(false);
    }
  };

  if (loading) return (
    <DashboardLayout wide>
      <div className="space-y-4 animate-pulse">
        <div className="h-20 bg-gray-200 rounded-2xl" />
        <div className="grid grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-28 bg-gray-200 rounded-2xl" />)}
        </div>
      </div>
    </DashboardLayout>
  );

  if (error) return (
    <DashboardLayout>
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
      <Link to="/admin/members" className="text-reset-terracotta text-sm hover:underline mt-3 inline-block">← Back</Link>
    </DashboardLayout>
  );

  const { member, reset_path, scores, checkins, nudges_sent, slip_status, integrations, notes, course_progress, communications, activity, payment, sequence_state } = data;

  // Build sector summary from latest scores
  const latestScores = SECTORS.map(s => {
    const scoreRows = scores.filter(r => r.sector === s.key);
    const latest    = scoreRows[0]; // already ordered desc
    const min = reset_path?.[`${s.key}_comfort_min`] ?? null;
    const max = reset_path?.[`${s.key}_comfort_max`] ?? null;
    return {
      sector:       s.key,
      combined_score:      latest ? parseFloat(latest.combined_score) : null,
      activity_score:      latest ? parseFloat(latest.activity_score) : null,
      physiological_score: latest ? parseFloat(latest.physiological_score) : null,
      in_comfort_zone:     latest?.in_comfort_zone ?? null,
      trend:               latest?.trend ?? 'stable',
      divergence_flag:     latest?.divergence_flag ?? false,
      comfort_min:         min != null ? parseFloat(min) : null,
      comfort_max:         max != null ? parseFloat(max) : null,
      status: !latest ? 'unknown'
        : latest.in_comfort_zone ? 'green'
        : latest.trend === 'declining' ? 'red' : 'amber',
    };
  });

  // Check-in calendar: last 14 days
  const checkinDates = new Set(checkins.map(c => c.checkin_date?.toString().slice(0, 10)));
  const last14 = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (13 - i));
    return d.toISOString().slice(0, 10);
  });

  const firstName = (member.name || '').split(' ')[0] || 'Member';

  return (
    <DashboardLayout>

      {/* Back nav */}
      <Link to="/admin/members" className="text-xs text-reset-terracotta hover:underline flex items-center gap-1 mb-5">
        ← All members
      </Link>

      {/* Member header */}
      <div className="bg-white rounded-2xl shadow-sm p-5 mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
        {member.avatar_url ? (
          <img src={member.avatar_url} alt={`${member.name || 'Member'} photo`} className="w-16 h-16 rounded-full object-cover shrink-0" />
        ) : (
          <span aria-hidden="true" className="w-16 h-16 rounded-full bg-reset-sage text-white flex items-center justify-center text-xl font-bold shrink-0">
            {(member.name || '?').trim().charAt(0).toUpperCase()}
          </span>
        )}
        <div>
          {editingProfile ? (
            <div className="space-y-2 mb-2">
              <input
                type="text"
                value={profileDraft.name}
                onChange={e => setProfileDraft(d => ({ ...d, name: e.target.value }))}
                placeholder="Name"
                className="block w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5"
              />
              <div>
                <input
                  type="email"
                  value={profileDraft.email}
                  onChange={e => setProfileDraft(d => ({ ...d, email: e.target.value }))}
                  placeholder="Email (this is what they sign in with)"
                  className="block w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5"
                />
                <p className="text-xs text-gray-400 mt-0.5">Changing this changes what they sign in with — they'll need to use the new address next time.</p>
              </div>
              <input
                type="text"
                value={profileDraft.phone}
                onChange={e => setProfileDraft(d => ({ ...d, phone: e.target.value }))}
                placeholder="Phone (optional)"
                className="block w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5"
              />
              <div className="flex gap-2">
                <button onClick={handleSaveProfile} disabled={savingProfile} className="text-xs font-medium bg-reset-terracotta text-white px-3 py-1.5 rounded-lg disabled:opacity-50">
                  {savingProfile ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => setEditingProfile(false)} disabled={savingProfile} className="text-xs font-medium text-gray-500 px-3 py-1.5">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <h1 className="font-serif text-xl font-bold text-reset-brown">{member.name || '(no name)'}</h1>
              <button
                onClick={() => { setProfileDraft({ name: member.name || '', phone: member.phone || '', email: member.email || '' }); setEditingProfile(true); }}
                className="text-xs font-medium text-reset-terracotta hover:underline mt-1.5"
              >
                Edit
              </button>
            </div>
          )}
          {!editingProfile && <p className="text-sm text-gray-500">{member.email}</p>}
          {!editingProfile && member.phone && <p className="text-xs text-gray-400">{member.phone}</p>}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              member.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {member.is_active ? 'Active subscription' : 'Inactive'}
            </span>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              member.onboarding_phase === 'active' ? 'bg-green-100 text-green-700' :
              member.onboarding_phase === 'path_set' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {member.onboarding_phase?.replace('_', ' ')}
            </span>
            {member.founder_pricing && (
              <span className="text-xs font-semibold text-reset-brown bg-reset-gold/40 border border-reset-gold px-2.5 py-1 rounded-full">
                ★ Founder
              </span>
            )}
            {editingJoinedAt ? (
              <span className="inline-flex items-center gap-1.5 text-xs">
                <input
                  type="date"
                  value={joinedAtDraft}
                  onChange={e => setJoinedAtDraft(e.target.value)}
                  className="border border-gray-200 rounded px-1.5 py-0.5 text-xs text-reset-brown focus:outline-none focus:ring-1 focus:ring-reset-terracotta"
                />
                <button onClick={handleSaveJoinedAt} disabled={savingJoinedAt} className="text-reset-terracotta font-medium hover:underline disabled:opacity-50">
                  {savingJoinedAt ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => setEditingJoinedAt(false)} className="text-gray-400 hover:text-reset-brown">Cancel</button>
              </span>
            ) : (
              <span className="text-xs text-gray-400">
                Joined {new Date(member.real_joined_at || member.created_at).toLocaleDateString()} · Day {member.day_in_program}
                <button
                  onClick={() => {
                    const d = member.real_joined_at || member.created_at;
                    setJoinedAtDraft(d ? new Date(d).toISOString().slice(0, 10) : '');
                    setEditingJoinedAt(true);
                  }}
                  className="ml-1.5 text-reset-terracotta hover:underline"
                >
                  Fix
                </button>
              </span>
            )}
            {member.days_until_renewal <= 30 && (
              <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                Renews in {member.days_until_renewal}d
              </span>
            )}
            <MemberGenderField memberId={id} />
            {member.referred_by_name && (
              <p className="text-xs text-gray-500 mt-1">Referred by (typed at signup): <span className="font-medium text-reset-brown">{member.referred_by_name}</span></p>
            )}
            {(member.how_heard || member.utm_source) && (
              <span
                className="text-xs bg-reset-sage/10 text-reset-sage px-2 py-0.5 rounded-full"
                title={[
                  member.how_heard && `Said: ${member.how_heard}`,
                  member.utm_source && `Tagged link: ${member.utm_source}${member.utm_campaign ? ` / ${member.utm_campaign}` : ''}`,
                ].filter(Boolean).join(' · ')}
              >
                Found us via {member.how_heard || member.utm_source}
              </span>
            )}
            {integrations.filter(i => i.is_active).map(i => (
              <span key={i.service} className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">
                {i.service.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
        </div>

        {/* Manual nudge + password reset + direct contact */}
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2 flex-wrap justify-end">
            <button
              onClick={openComposer}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors"
            >
              ✉️ Send email
            </button>
            <button
              disabled
              title="Not connected yet — SMS needs Twilio set up, deliberately held until closer to launch"
              className="px-4 py-2 bg-gray-100 text-gray-400 text-sm font-medium rounded-xl cursor-not-allowed"
            >
              💬 Send text (not connected)
            </button>
            <button
              onClick={handleSendReset}
              disabled={sendingReset || resetSent}
              className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:border-reset-terracotta hover:text-reset-terracotta transition-colors disabled:opacity-50"
            >
              {sendingReset ? 'Sending…' : resetSent ? '✓ Reset link sent' : '🔑 Send reset link'}
            </button>
            <button
              onClick={() => setShowTempPassword(v => !v)}
              className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
              title="For when a real reset email genuinely won't reach them"
            >
              🔧 Set temp password
            </button>
            <button
              onClick={handleNudge}
              disabled={nudging || nudgeSent}
              className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:border-reset-terracotta hover:text-reset-terracotta transition-colors disabled:opacity-50"
            >
              {nudging ? 'Sending…' : nudgeSent ? '✓ Nudge sent' : '📩 Send nudge'}
            </button>
          </div>
          {member.ghl_contact_id && (
            <span className="text-[10px] text-gray-400">GHL: {member.ghl_contact_id.slice(0, 8)}…</span>
          )}
          <div className="flex gap-2 flex-wrap justify-end mt-1">
            <button
              onClick={handleToggleActive}
              disabled={archiving}
              className="px-3 py-1.5 border border-gray-200 text-gray-500 text-xs font-medium rounded-lg hover:border-amber-300 hover:text-amber-600 transition-colors disabled:opacity-50"
            >
              {member.is_active ? 'Archive' : 'Reactivate'}
            </button>
            {archivePanel && member.is_active && (
              <div className="w-full mt-2 border border-gray-200 rounded-xl p-3 space-y-2 text-left bg-white">
                <p className="text-xs text-gray-600">Archive {member.name || 'this member'}? They'll show as inactive everywhere, same as a lapsed or cancelled member. Nothing is deleted.</p>
                <select
                  value={leaveReason} onChange={e => setLeaveReason(e.target.value)}
                  aria-label="Why are they leaving? (optional)"
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-reset-brown"
                >
                  <option value="">Why are they leaving? (optional)</option>
                  <option value="price">The price</option>
                  <option value="no_time">No time right now</option>
                  <option value="not_using">Not using it enough</option>
                  <option value="life_change">Something changed in their life</option>
                  <option value="other">Something else</option>
                </select>
                <textarea
                  value={leaveNote} onChange={e => setLeaveNote(e.target.value)} rows={2}
                  placeholder="Anything to remember (optional)"
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs resize-none"
                />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setArchivePanel(false)} className="px-3 py-1 text-xs text-gray-500 hover:text-reset-brown">Cancel</button>
                  <button type="button" onClick={handleToggleActive} disabled={archiving}
                    className="px-3 py-1 text-xs font-medium rounded-lg bg-reset-terracotta text-white hover:bg-reset-brown disabled:opacity-50">
                    {archiving ? 'Archiving…' : 'Archive'}
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={() => setShowMerge(true)}
              className="px-3 py-1.5 border border-gray-200 text-gray-500 text-xs font-medium rounded-lg hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
            >
              Merge into another member
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-3 py-1.5 border border-gray-200 text-gray-500 text-xs font-medium rounded-lg hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-50"
            >
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </button>
          </div>
        </div>
      </div>

      {showMerge && (
        <MergeMemberPicker
          currentMemberId={id}
          currentMemberName={member.name || member.email}
          merging={merging}
          onMerge={handleMerge}
          onCancel={() => setShowMerge(false)}
        />
      )}

      {showTempPassword && (
        <TempPasswordModal
          memberId={id}
          memberName={member.name || member.email}
          onDone={() => setShowTempPassword(false)}
          onCancel={() => setShowTempPassword(false)}
        />
      )}

      {/* Slip status alert */}
      {slip_status?.flagged && (
        <div className="mb-5 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <span className="text-xl">🚨</span>
          <div>
            <p className="text-sm font-semibold text-red-800">Slip detected</p>
            <p className="text-xs text-red-600">{slip_status.reason}</p>
          </div>
        </div>
      )}
      {slip_status?.onCooldown && (
        <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          ⏸ Slip nudge cooldown active — last slip nudge sent in the past 7 days.
        </div>
      )}

      {/* Member page, Jenn 2026-10-28 ("fabulous ... similar to our affiliate controls"):
          three columns like the approved design: who they are | what has happened | where they stand. */}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,290px)_minmax(0,1fr)_minmax(0,360px)] gap-5 items-start">
        <div className="space-y-5 min-w-0">
          <ProfileColumn member={member} profile={data.profile} memberId={id} onTagsChanged={tags => setData(d => ({ ...d, profile: { ...d.profile, tags } }))} />
        </div>
        <div className="space-y-5 min-w-0">
        {/* Staff notes */}
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-reset-brown mb-4">Notes</h3>
          <div className="flex gap-2 mb-4">
            <input
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddNote(); }}
              placeholder="Add a note…"
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
            <button
              onClick={handleAddNote}
              disabled={savingNote || !newNote.trim()}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
            >
              Add
            </button>
          </div>
          {notes.length === 0 ? (
            <p className="text-sm text-gray-400">No notes yet.</p>
          ) : (
            <div className="space-y-3">
              {notes.map(n => (
                <div key={n.id} className="text-sm border-b border-gray-50 last:border-0 pb-3 last:pb-0">
                  <p className="text-reset-brown">{n.body}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-gray-400">
                      {n.author_name} · {new Date(n.created_at).toLocaleString(undefined, { hour12: true })}
                    </span>
                    <button onClick={() => handleDeleteNote(n.id)} className="text-[10px] text-red-400 hover:text-red-600">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Communications — every email this app has sent about this member.
            Permanent, visible to every Team365 admin — no delete exists for
            this table, deliberately (see memberCommunication.model.js). */}
        <section ref={commsRef} className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-reset-brown">Communications</h3>
            {!composing && (
              <button onClick={() => setComposing(true)} className="text-xs font-medium text-reset-terracotta hover:underline">
                + Message member
              </button>
            )}
          </div>

          {composing && (
            <div className="mb-4 border border-gray-100 rounded-xl p-3 space-y-2">
              <input
                value={msgSubject} onChange={e => setMsgSubject(e.target.value)}
                placeholder="Subject"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
              />
              <textarea
                value={msgBody} onChange={e => setMsgBody(e.target.value)}
                placeholder={`Message to ${firstName}…`}
                rows={5}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
              />
              <div>
                <label className="block text-[10px] font-medium text-gray-400 mb-1">Links (optional — one per line, "Label | URL")</label>
                <textarea
                  value={msgLinksText} onChange={e => setMsgLinksText(e.target.value)}
                  placeholder="Worksheet | https://..."
                  rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                />
              </div>
              <p className="text-[10px] text-gray-400">Sends from team365@365reset.com. Permanently logged below for every Team365 admin to see — this can't be deleted later.</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setComposing(false); setMsgSubject(''); setMsgBody(''); setMsgLinksText(''); }} className="px-3 py-1.5 text-sm text-gray-500 hover:text-reset-brown">
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={sendingMsg || !msgSubject.trim() || !msgBody.trim()}
                  className="px-4 py-1.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
                >
                  {sendingMsg ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          )}

          {communications.length === 0 ? (
            <p className="text-sm text-gray-400">No emails sent yet.</p>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {communications.map(c => (
                <div key={c.id} className="text-sm py-1.5 border-b border-gray-50 last:border-0">
                  <button
                    onClick={() => {
                      if (c.html) { setPreviewComm(c); setForwardTo(''); setForwardResult(null); return; }
                      if (c.body) setExpandedCommId(expandedCommId === c.id ? null : c.id);
                    }}
                    className={`w-full flex flex-wrap items-start justify-between gap-x-2 gap-y-0.5 text-left ${c.html || c.body ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <span className="text-reset-brown min-w-0 flex-1 basis-48 break-words">
                      {c.subject || COMMUNICATION_LABELS[c.type] || c.type}
                      {c.type !== 'admin_message' && (
                        <span className="text-gray-400"> · {COMMUNICATION_LABELS[c.type] || c.type}</span>
                      )}
                      {/* Which day of their sequence this email was for — Team365 only,
                          never shown to the member (Jenn, 2026-09-24). */}
                      {c.sequence_day != null && (
                        <span className="ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-reset-terracotta/10 text-reset-terracotta align-middle">Day {c.sequence_day}</span>
                      )}
                    </span>
                    <span className="text-xs text-gray-400 shrink-0 flex items-center gap-1.5">
                      {c.html && <span className="text-reset-terracotta font-medium">View →</span>}
                      {new Date(c.sent_at).toLocaleString(undefined, { hour12: true })}
                    </span>
                  </button>
                  {/* Older communications sent before we started saving the real
                      HTML fall back to the plain-text recap inline. */}
                  {!c.html && expandedCommId === c.id && c.body && (
                    <p className="text-xs text-gray-600 whitespace-pre-wrap mt-1.5 bg-reset-cream rounded-lg p-2.5">{c.body}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Real email preview — the exact HTML that was sent, not a text
            recap. Jenn, 2026-08-31: "I want to actually see the format and
            what they received. In its entirety." */}
        {previewComm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPreviewComm(null)}>
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-reset-brown truncate">{previewComm.subject || COMMUNICATION_LABELS[previewComm.type] || previewComm.type}</p>
                  <p className="text-xs text-gray-400">{new Date(previewComm.sent_at).toLocaleString(undefined, { hour12: true })}</p>
                </div>
                <button onClick={() => setPreviewComm(null)} className="text-gray-300 hover:text-reset-brown text-xl leading-none px-2 shrink-0">×</button>
              </div>
              <iframe
                title="Email preview"
                srcDoc={previewComm.html}
                // See AnnouncementDetail.jsx's email nudge iframe — a fully
                // empty sandbox silently failed to load embedded images.
                sandbox="allow-same-origin"
                className="flex-1 w-full bg-white"
                style={{ minHeight: '60vh' }}
              />
              <div className="border-t border-gray-100 px-5 py-3 shrink-0 rounded-b-2xl bg-reset-cream/40">
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={forwardTo}
                    onChange={e => { setForwardTo(e.target.value); setForwardResult(null); }}
                    placeholder="Forward this exact email to…"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                  />
                  <button
                    onClick={handleForwardComm}
                    disabled={forwarding || !forwardTo.trim()}
                    className="px-4 py-1.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50 shrink-0"
                  >
                    {forwarding ? 'Forwarding…' : 'Forward'}
                  </button>
                </div>
                {forwardResult?.ok && <p className="text-xs text-reset-sage mt-1.5">Forwarded.</p>}
                {forwardResult && !forwardResult.ok && <p className="text-xs text-red-500 mt-1.5">{forwardResult.error}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Activity — the member's own actions, from the existing audit log */}
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-reset-brown mb-4">Activity</h3>
          {activity.length === 0 ? (
            <p className="text-sm text-gray-400">No activity recorded yet.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {activity.map(a => (
                <div key={a.id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                  <span className="text-reset-brown">{ACTIVITY_LABELS[a.action] || a.action.replace(/_/g, ' ').toLowerCase()}</span>
                  <span className="text-xs text-gray-400">{new Date(a.created_at).toLocaleString(undefined, { hour12: true })}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Check-in calendar */}
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-reset-brown mb-4">Check-ins — last 14 days</h3>
          {!data.personal_data_shared ? (
            <p className="text-sm text-gray-400">🔒 Private — not shared with you.</p>
          ) : (
          <>

          <div className="grid grid-cols-7 gap-1.5">
            {last14.map(date => {
              const hasCheckin = checkinDates.has(date);
              const dayLabel   = new Date(date + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'narrow' });
              return (
                <div key={date} className="flex flex-col items-center gap-1">
                  <span className="text-[9px] text-gray-400 uppercase">{dayLabel}</span>
                  <span
                    title={date}
                    className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-medium ${
                      hasCheckin
                        ? 'bg-reset-terracotta text-white'
                        : 'bg-gray-100 text-gray-300'
                    }`}
                  >
                    {new Date(date + 'T12:00:00').getDate()}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            {checkins.length} check-in{checkins.length !== 1 ? 's' : ''} in 14 days
          </p>
          </>
          )}
        </section>

        {/* Recent nudges */}
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-reset-brown mb-4">Recent Nudges</h3>
          {nudges_sent.length === 0 ? (
            <p className="text-sm text-gray-400">No nudges sent yet.</p>
          ) : (
            <div className="space-y-2">
              {nudges_sent.slice(0, 10).map(n => (
                <div key={n.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${
                      n.nudge_type === 'slip_detection' ? 'bg-red-400' :
                      n.nudge_type === 'morning'         ? 'bg-green-400' : 'bg-blue-400'
                    }`} />
                    <span className="text-gray-600 capitalize">{n.nudge_type?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400">
                      {new Date(n.sent_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className={n.delivered ? 'text-green-500' : 'text-gray-300'}>
                      {n.delivered ? '✓' : '○'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Course progress */}
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-reset-brown mb-4">Course Progress</h3>
          {course_progress.length === 0 ? (
            <p className="text-sm text-gray-400">Not enrolled in any courses yet.</p>
          ) : (
            <div className="space-y-3">
              {course_progress.map(c => (
                <CourseProgressRow key={c.id} course={c} memberId={id} onFixed={load} />
              ))}
            </div>
          )}
        </section>

        </div>
        <div className="space-y-5 min-w-0">
        <section className="bg-white rounded-2xl shadow-sm p-5">
        {/* Sector scores — shared with Jenn by default; hidden if the member
            turned sharing off, and always hidden from Patti/Trish regardless
            of the member's setting. */}
        {!data.personal_data_shared ? (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center gap-3">
            <span className="text-xl">🔒</span>
            <div>
              <p className="text-sm font-semibold text-gray-700">Check-in, Compass &amp; Continuum data is private</p>
              <p className="text-xs text-gray-500">
                {member.data_shared_with_mentor
                  ? 'This is shared with Jenn only — it isn\'t visible to other staff.'
                  : 'This member has turned off sharing this with staff.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Latest Sector Scores</h2>
            <div className="grid grid-cols-2 gap-3">
              {latestScores.map(s => (
                <SectorScoreCard
                  key={s.sector}
                  sectorData={s}
                  isPriority={reset_path?.priority_sectors?.[0] === s.sector}
                />
              ))}
            </div>
          </>
        )}
        </section>

        {/* Payment — frequency/amount/coupon/trial from Stripe, never card details */}
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-reset-brown mb-4">Payment</h3>

          {/* Product & membership type — which product this member is on and
              how it's paid for. 'paid' means the real Stripe subscription
              below is the source of truth; 'trial'/'free_annual' are
              Team365-granted arrangements outside Stripe. */}
          <div className="mb-4 pb-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Product & membership</span>
              {!editingMembership && (
                <button onClick={() => setEditingMembership(true)} className="text-xs font-medium text-reset-terracotta hover:underline">
                  Edit
                </button>
              )}
            </div>
            {membershipSyncNote && (
              <div className={`mb-2 text-xs rounded-lg px-2.5 py-2 ${membershipSyncNote.startsWith('Could not') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {membershipSyncNote}
                {membershipSyncNote.startsWith('Could not') && ' A to-do has been created to follow up.'}
              </div>
            )}
            {editingMembership ? (
              <MembershipForm
                current={{ product_name: member.product_name, membership_type: member.membership_type, membership_expires_at: member.membership_expires_at }}
                saving={savingMembership}
                onSave={handleSaveMembership}
                onCancel={() => setEditingMembership(false)}
              />
            ) : (
              <p className="text-sm text-reset-brown">
                {member.product_name || '365reset'}
                <span className="text-gray-400"> · {MEMBERSHIP_TYPE_LABELS[member.membership_type] || member.membership_type || 'Paid'}</span>
                {member.membership_expires_at && (
                  <span className="block text-xs text-gray-400 mt-0.5">
                    {member.membership_type === 'paid' ? 'Expires' : 'Ends'} {new Date(member.membership_expires_at).toLocaleDateString()}
                  </span>
                )}
                {member.membership_expiry_flagged_at && (member.membership_type === 'trial' || member.membership_type === 'free_annual') && (
                  <span className="block text-xs font-medium text-red-600 mt-1">
                    ⚠ Free period ended {new Date(member.membership_expiry_flagged_at).toLocaleDateString()} — flagged in To-Do's, no card on file to convert automatically.
                  </span>
                )}
              </p>
            )}
          </div>

          {!member.has_stripe_customer ? (
            <p className="text-sm text-gray-400">No Stripe customer linked to this member.</p>
          ) : !payment ? (
            <p className="text-sm text-gray-400">Payment details aren't available right now.</p>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Status</span>
                <span className="text-reset-brown font-medium capitalize">{payment.status?.replace('_', ' ')}</span>
              </div>
              {payment.amount != null && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Amount</span>
                  <span className="text-reset-brown font-medium">
                    {formatMoney(payment.amount, payment.currency)}
                    {payment.interval ? ` / ${payment.intervalCount > 1 ? `${payment.intervalCount} ` : ''}${payment.interval}${payment.intervalCount > 1 ? 's' : ''}` : ''}
                  </span>
                </div>
              )}
              {payment.trialEnd && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Trial ends</span>
                  <span className="text-amber-600 font-medium">{new Date(payment.trialEnd).toLocaleDateString()}</span>
                </div>
              )}
              {payment.coupon && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Coupon</span>
                  <span className="text-reset-terracotta font-medium">
                    {payment.coupon.name}
                    {payment.coupon.percentOff ? ` (${payment.coupon.percentOff}% off)` : ''}
                    {payment.coupon.amountOff ? ` (${formatMoney(payment.coupon.amountOff, payment.currency)} off)` : ''}
                    {payment.coupon.end ? ` until ${new Date(payment.coupon.end).toLocaleDateString()}` : ' — indefinite'}
                  </span>
                </div>
              )}
              {payment.cancelAt && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Cancels</span>
                  <span className="text-red-600 font-medium">{new Date(payment.cancelAt).toLocaleDateString()}</span>
                </div>
              )}
              {payment.currentPeriodEnd && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Next renewal</span>
                  <span className="text-reset-brown font-medium">{new Date(payment.currentPeriodEnd).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          )}

          {/* Founder pricing — our own record of a locked-in deal (coupon/early
              members), not connected to Stripe. See admin.controller.js's
              updateFounderPricing comment for why the real-money enforcement
              side of this isn't built yet. */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Founder pricing</span>
              {!editingFounderPricing && (
                <button onClick={() => setEditingFounderPricing(true)} className="text-xs font-medium text-reset-terracotta hover:underline">
                  {member.founder_pricing ? 'Edit' : '+ Set'}
                </button>
              )}
            </div>
            {editingFounderPricing ? (
              <FounderPricingForm
                current={member.founder_pricing}
                saving={savingFounderPricing}
                onSave={handleSaveFounderPricing}
                onCancel={() => setEditingFounderPricing(false)}
              />
            ) : member.founder_pricing ? (
              <p className="text-sm text-reset-brown">
                {formatMoney(member.founder_pricing.amount, member.founder_pricing.currency)} / {member.founder_pricing.interval}
                {member.founder_pricing.expires_at && ` until ${new Date(member.founder_pricing.expires_at).toLocaleDateString()}`}
                {member.founder_pricing.note && <span className="block text-xs text-gray-400 mt-0.5">{member.founder_pricing.note}</span>}
                <span className="block text-[10px] text-amber-600 mt-1">Record only — not yet connected to what Stripe actually charges.</span>
              </p>
            ) : (
              <p className="text-sm text-gray-400">No locked-in pricing on file.</p>
            )}
          </div>
        </section>

        <SalesMarketingAccessCard member={member} />

        {/* Sequence position — where this member currently is in each content
            sequence. The daily send engine reads and advances the "daily"
            row for anyone with daily_content_enabled on; the admin override
            Jenn confirmed is essential for real member migration and manual
            correction stays available regardless. */}
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-reset-brown mb-1">📧 Real Daily Send</h3>
          <p className="text-xs text-gray-400 mb-3">
            {member.daily_content_enabled
              ? `On — this member gets today's real daily email, sent automatically. Moved in ${member.daily_content_enrolled_at ? new Date(member.daily_content_enrolled_at).toLocaleDateString() : 'recently'}.`
              : "Off — this member is not receiving the new system's daily content. Turn on to move them into the new system's real send."}
          </p>
          <button
            onClick={() => handleToggleDailyContent(!member.daily_content_enabled)}
            disabled={savingDailyEnabled}
            className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 ${
              member.daily_content_enabled
                ? 'border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-500'
                : 'bg-reset-terracotta text-white hover:bg-reset-brown'
            }`}
          >
            {savingDailyEnabled ? 'Saving…' : member.daily_content_enabled ? 'Turn off daily send' : 'Move into new system — turn on daily send'}
          </button>

          {/* Deliberately muted/secondary — these don't send anything yet
              (no engine built), and being visually similar to Daily Send
              above is exactly what caused Jenn to mistake this for the
              real daily-email toggle, 2026-08-30. */}
          <div className="mt-6 pt-4 border-t border-gray-100 bg-gray-50 -mx-5 -mb-5 px-5 pb-5 rounded-b-2xl">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-3">Not active yet — no send engine built for these</p>

            <h3 className="text-xs font-medium text-gray-500 mb-1">Onboarding Send</h3>
            <p className="text-[11px] text-gray-400 mb-2">
              {member.onboarding_content_enabled
                ? 'On, but inert — no onboarding send engine exists yet.'
                : 'Off — existing/migrated members should stay off permanently.'}
            </p>
            <button
              onClick={() => handleToggleOnboardingContent(!member.onboarding_content_enabled)}
              disabled={savingOnboardingEnabled}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-500 hover:border-gray-300 transition-colors disabled:opacity-50"
            >
              {savingOnboardingEnabled ? 'Saving…' : member.onboarding_content_enabled ? 'Turn off' : 'Turn on'}
            </button>

            <h3 className="text-xs font-medium text-gray-500 mt-4 mb-1">Renewal Send</h3>
            <p className="text-[11px] text-gray-400 mb-2">
              {member.renewal_content_enabled
                ? 'On, but inert — no renewal send engine exists yet.'
                : 'Off — should only turn on once someone reaches their real renewal window.'}
            </p>
            <button
              onClick={() => handleToggleRenewalContent(!member.renewal_content_enabled)}
              disabled={savingRenewalEnabled}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-500 hover:border-gray-300 transition-colors disabled:opacity-50"
            >
              {savingRenewalEnabled ? 'Saving…' : member.renewal_content_enabled ? 'Turn off' : 'Turn on'}
            </button>
          </div>

          <h3 className="text-sm font-semibold text-reset-brown mt-6 mb-1">Content Sequence Position</h3>
          <p className="text-xs text-gray-400 mb-4">Which day of onboarding/daily/renewal content this member is on.</p>
          <div className="space-y-2">
            {(sequence_state || []).map(s => (
              <div key={s.sequence_type} className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{SEQUENCE_LABELS[s.sequence_type] || s.sequence_type}</span>
                {editingSequenceType === s.sequence_type ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="1" value={sequenceDayDraft}
                      onChange={e => setSequenceDayDraft(e.target.value)}
                      className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                      autoFocus
                    />
                    <button onClick={() => setEditingSequenceType(null)} className="text-xs text-gray-400 hover:text-reset-brown">Cancel</button>
                    <button
                      onClick={() => handleSaveSequenceDay(s.sequence_type)} disabled={savingSequenceDay}
                      className="text-xs font-medium text-reset-terracotta hover:underline disabled:opacity-50"
                    >
                      {savingSequenceDay ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-reset-brown font-medium">Day {s.current_day}</span>
                    <button
                      onClick={() => { setEditingSequenceType(s.sequence_type); setSequenceDayDraft(String(s.current_day)); setError(null); }}
                      className="text-xs font-medium text-reset-terracotta hover:underline"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Affiliate/Royalty — Jenn, 2026-09-09: "I want to see from a member
            profile view whether they are an affiliate," "be able to make a
            member an affiliate from their member profile view," and "see
            which clients are linked to them." */}
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-reset-brown mb-3">Affiliate / Royalty</h3>

          {member.referred_by ? (
            <p className="text-sm text-reset-brown mb-3">
              Referred by <span className="font-medium">{member.referred_by.name}</span>
              {member.referred_by.referral_code && <span className="text-gray-400"> · code: <span className="font-mono">{member.referred_by.referral_code}</span></span>}
            </p>
          ) : (
            <AssignReferrerControl memberId={id} onAssigned={load} />
          )}

          {member.affiliate_partnerships?.length > 0 ? (
            <div className="space-y-2">
              {member.affiliate_partnerships.map(p => (
                <Link key={p.id} to={`/admin/affiliates/${p.id}`}
                  className="block text-sm border border-gray-100 rounded-xl px-3 py-2 hover:border-reset-terracotta transition-colors">
                  <span className="capitalize font-medium text-reset-brown">{p.partner_type}</span>
                  <span className="text-gray-400"> · {p.commission_rate_pct}%</span>
                  {p.referral_code && <span className="text-gray-400"> · code: <span className="font-mono">{p.referral_code}</span></span>}
                  {!p.is_active && <span className="text-amber-600"> · inactive</span>}
                  <span className="text-reset-terracotta float-right">View →</span>
                </Link>
              ))}
            </div>
          ) : makingAffiliate ? (
            <div className="border border-gray-100 rounded-xl p-3 space-y-2">
              <select value={affiliateDraft.partnerType}
                onChange={e => setAffiliateDraft(d => ({ ...d, partnerType: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <option value="affiliate">Affiliate</option>
                <option value="royalty">Royalty</option>
              </select>
              {affiliateDraft.partnerType === 'affiliate' && (
                <input value={affiliateDraft.referralCode}
                  onChange={e => setAffiliateDraft(d => ({ ...d, referralCode: e.target.value }))}
                  placeholder="Referral code, e.g. patti"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              )}
              <input type="number" step="0.01" value={affiliateDraft.commissionRatePct}
                onChange={e => setAffiliateDraft(d => ({ ...d, commissionRatePct: e.target.value }))}
                placeholder="Commission %"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              {affiliateError && <p className="text-xs text-red-500">{affiliateError}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={() => { setMakingAffiliate(false); setAffiliateError(null); }} className="px-3 py-1.5 text-sm text-gray-500 hover:text-reset-brown">Cancel</button>
                <button onClick={handleMakeAffiliate} disabled={savingAffiliate}
                  className="px-4 py-1.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
                  {savingAffiliate ? 'Saving…' : 'Create'}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setMakingAffiliate(true)} className="text-xs font-medium text-reset-terracotta hover:underline">
              + Make this member an affiliate
            </button>
          )}
        </section>

        {/* Send Exercises To Me — activity opt-in/opt-out, admin view + edit */}
        <ActivityPreferencesSection memberId={id} />

        {/* Mentoring — lead mentor, Mentoring Strategy (mentors only), mentor access (Chief Mentor only). Migration 145. */}
        <MentoringSection memberId={id} data={data} onChanged={load} />

        </div>
      </div>

    </DashboardLayout>
  );
}

// ─── Course progress row — with a real quick-adjust control ───────────────
// Jenn, 2026-08-30: "we need to be able to fix things that didn't cross
// over correctly" — real migrated data won't always be exactly right, so
// staff need a fast way to correct a member's lesson position right here.

function CourseProgressRow({ course, memberId, onFixed }) {
  const total = Number(course.lesson_count) || 0;
  const done  = Number(course.completed_count) || 0;
  const pct   = total > 0 ? Math.round((done / total) * 100) : 0;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(done);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await setMemberCourseProgress(memberId, course.id, Number(draft));
      setEditing(false);
      onFixed();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-reset-brown font-medium">{course.title}</span>
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input
              type="number" min="0" max={total} value={draft}
              onChange={e => setDraft(e.target.value)}
              className="w-16 border border-gray-200 rounded-lg px-2 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
            <span className="text-xs text-gray-400">/ {total}</span>
            <button onClick={save} disabled={saving} className="text-xs font-medium text-reset-terracotta hover:underline disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="text-xs text-gray-400 hover:text-reset-brown">Cancel</button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{done}/{total} lessons</span>
            <button onClick={() => { setDraft(done); setEditing(true); }} className="text-xs font-medium text-reset-terracotta hover:underline">
              Fix
            </button>
          </div>
        )}
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full bg-reset-terracotta rounded-full" style={{ width: `${pct}%` }} />
      </div>
      {course.last_accessed_at && (
        <p className="text-[10px] text-gray-400 mt-1">
          Last opened {new Date(course.last_accessed_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}

// ─── Product & membership edit form ────────────────────────────────────────

function MembershipForm({ current, saving, onSave, onCancel }) {
  const [productName, setProductName] = useState(current?.product_name || '365reset');
  const [membershipType, setMembershipType] = useState(current?.membership_type || 'paid');
  const [expiresAt, setExpiresAt] = useState(current?.membership_expires_at ? current.membership_expires_at.slice(0, 10) : '');

  const submit = (e) => {
    e.preventDefault();
    onSave({
      productName: productName.trim() || '365reset',
      membershipType,
      expiresAt: expiresAt || null,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <input
        type="text" value={productName} onChange={e => setProductName(e.target.value)}
        placeholder="365reset"
        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
      />
      <select
        value={membershipType} onChange={e => setMembershipType(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
      >
        <option value="paid">Paid</option>
        <option value="trial">Free trial</option>
        <option value="free_annual">Free 1-year membership</option>
        <option value="royalty">Royalty member (free)</option>
      </select>
      {membershipType !== 'paid' && membershipType !== 'royalty' && (
        <input
          type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      )}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="text-xs font-medium text-reset-terracotta hover:underline disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="text-xs text-gray-400 hover:text-reset-brown">Cancel</button>
      </div>
    </form>
  );
}

// ─── Founder pricing edit form ─────────────────────────────────────────────

function FounderPricingForm({ current, saving, onSave, onCancel }) {
  const [amount, setAmount] = useState(current?.amount ?? '');
  const [intervalUnit, setIntervalUnit] = useState(current?.interval || 'year');
  const [expiresAt, setExpiresAt] = useState(current?.expires_at ? current.expires_at.slice(0, 10) : '');
  const [note, setNote] = useState(current?.note || '');

  const submit = (e) => {
    e.preventDefault();
    onSave({
      amount: amount === '' ? null : Number(amount),
      interval: amount === '' ? null : intervalUnit,
      expiresAt: expiresAt || null,
      note: note.trim() || null,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <div className="flex gap-2">
        <input
          type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)}
          placeholder="49.95"
          className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
        <select
          value={intervalUnit} onChange={e => setIntervalUnit(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        >
          <option value="year">/ year</option>
          <option value="month">/ month</option>
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Expires (leave blank if indefinite)</label>
        <input
          type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>
      <input
        value={note} onChange={e => setNote(e.target.value)}
        placeholder="Note (e.g. coupon code, why)"
        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
      />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-500 hover:text-reset-brown">
          Cancel
        </button>
        <button
          type="submit" disabled={saving}
          className="px-4 py-1.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

/**
 * Jenn, 2026-09-10: "I have members who were referred by affiliates were in
 * the old system... they didn't come through stripe. So I need to assign
 * them to an affiliate." Backfill-only — never overwrites an existing
 * referrer, same first-attribution rule the automatic Stripe path uses.
 * Doesn't retroactively credit past commissions, only future ones — said
 * plainly after assigning so it's never assumed to have done more than it did.
 */
function AssignReferrerControl({ memberId, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [partners, setPartners] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const startOpen = () => {
    setOpen(true);
    if (!partners) {
      listAffiliatePartners().then(all => setPartners(all.filter(p => p.partner_type === 'affiliate' && p.is_active !== false)))
        .catch(() => setError('Could not load affiliates.'));
    }
  };

  const handleAssign = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    try {
      await setMemberReferrer(memberId, selectedId);
      setDone(true);
      onAssigned();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not assign this referrer.');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return <p className="text-sm text-reset-sage mb-3">Assigned — future payments will credit this affiliate. Past payments before today aren't retroactively credited.</p>;
  }

  if (!open) {
    return (
      <button onClick={startOpen} className="text-xs font-medium text-reset-terracotta hover:underline mb-3">
        + Assign who referred this member
      </button>
    );
  }

  return (
    <div className="border border-gray-100 rounded-xl p-3 mb-3 space-y-2">
      <p className="text-xs text-gray-500">For members who came from the old system before real Stripe attribution existed. Only sets it if nothing's on file yet.</p>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {partners == null ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : (
        <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="">Choose an affiliate…</option>
          {partners.map(p => <option key={p.id} value={p.id}>{p.name}{p.referral_code ? ` (${p.referral_code})` : ''}</option>)}
        </select>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-sm text-gray-500 hover:text-reset-brown">Cancel</button>
        <button onClick={handleAssign} disabled={saving || !selectedId}
          className="px-4 py-1.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
          {saving ? 'Assigning…' : 'Assign'}
        </button>
      </div>
    </div>
  );
}

/**
 * Search-and-pick the survivor for a merge — same "search all members by
 * name/email" pattern as the affiliate partner form. Excludes the member
 * currently being merged away so it's never offered as its own survivor.
 */
function MergeMemberPicker({ currentMemberId, currentMemberName, merging, onMerge, onCancel }) {
  const [members, setMembers] = useState([]);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    getAdminMembers().then(m => { setMembers((m?.members || []).filter(x => x.id !== currentMemberId)); setMembersLoaded(true); })
      .catch(() => setMembersLoaded(true));
  }, [currentMemberId]);

  const matches = query.trim().length === 0 ? [] : members.filter(m =>
    (m.name || '').toLowerCase().includes(query.trim().toLowerCase()) ||
    (m.email || '').toLowerCase().includes(query.trim().toLowerCase())
  ).slice(0, 8);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-5 max-w-md w-full">
        <h3 className="font-semibold text-reset-brown mb-1">Merge {currentMemberName} into…</h3>
        <p className="text-xs text-gray-500 mb-3">
          Pick the member record to keep. Everything from {currentMemberName} — check-ins, scores, goals,
          notes, milestones — moves onto whoever you pick here, then {currentMemberName}'s record is deleted.
        </p>
        <div className="relative mb-3">
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setSelected(null); }}
            placeholder={membersLoaded ? 'Search members by name or email…' : 'Loading members…'}
            autoFocus
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          {matches.length > 0 && !selected && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
              {matches.map(m => (
                <button type="button" key={m.id}
                  onClick={() => { setSelected(m); setQuery(m.name || m.email); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-reset-cream border-b border-gray-50 last:border-0">
                  <span className="font-medium text-reset-brown">{m.name || '(no name)'}</span>
                  <span className="text-gray-400 ml-2">{m.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {selected && (
          <p className="text-xs text-reset-sage mb-3">✓ Will keep {selected.name || selected.email} — {selected.email}</p>
        )}
        <div className="flex items-center gap-3">
          <button
            onClick={() => onMerge(selected)}
            disabled={!selected || merging}
            className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
          >
            {merging ? 'Merging…' : 'Merge'}
          </button>
          <button onClick={onCancel} className="text-sm text-gray-500 hover:text-reset-brown">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Fallback for when a real reset-link email genuinely won't reach someone
 * (spam filtering, a typo'd address on file, a broken inbox) — sets a
 * password directly rather than relying on email delivery. This is the
 * one place in the app an admin chooses a password for someone else,
 * deliberately narrow: it's a stopgap, not a replacement for the normal
 * self-service reset flow, so the member should still change it
 * themselves once they're in.
 */
function TempPasswordModal({ memberId, memberName, onDone, onCancel }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await setMemberTempPassword(memberId, password);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not set this password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-5 max-w-sm w-full">
        {done ? (
          <>
            <h3 className="font-semibold text-reset-brown mb-2">Password set for {memberName}</h3>
            <p className="text-sm text-gray-600 mb-4">
              Share this password with them directly (not by email, if that's what's been failing) —
              they can sign in with it now, and should change it themselves once they're in.
            </p>
            <div className="bg-reset-cream rounded-lg px-3 py-2 font-mono text-sm text-reset-brown mb-4 select-all">
              {password}
            </div>
            <button onClick={onDone} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors">
              Done
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <h3 className="font-semibold text-reset-brown mb-1">Set a temporary password for {memberName}</h3>
            <p className="text-xs text-gray-500 mb-3">
              Use this only when a real reset email genuinely isn't reaching them. They should still
              change it themselves once they're in.
            </p>
            <input
              type="text"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Type a password (8+ characters)"
              minLength={8}
              required
              autoFocus
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono mb-3 focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
            {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
            <div className="flex items-center gap-3">
              <button type="submit" disabled={saving || password.length < 8} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
                {saving ? 'Setting…' : 'Set password'}
              </button>
              <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:text-reset-brown">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


// ─── Mentoring (migration 145, Jenn 2026-09-30) ──────────────────────────────
// Team-only. The server only sends mentoring_strategy to a viewer who is a
// mentor, so a non-mentor admin sees the lead mentor's name and nothing else.
function MentoringSection({ memberId, data, onChanged }) {
  const { member, mentors = [], viewer_is_mentor, viewer_is_chief_mentor, mentoring_strategy } = data;
  const [draft, setDraft]       = useState(mentoring_strategy?.body || '');
  const [saving, setSaving]     = useState(false);
  const [savedAt, setSavedAt]   = useState(null);
  const [err, setErr]           = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { setDraft(mentoring_strategy?.body || ''); }, [mentoring_strategy?.body]);

  const dirty = draft !== (mentoring_strategy?.body || '');

  const handleSave = async () => {
    setSaving(true); setErr(null);
    try {
      await saveMentoringStrategy(memberId, draft);
      setSavedAt(new Date());
      onChanged();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not save. Your text is still here, so try again.');
    } finally { setSaving(false); }
  };

  const handleLead = async (value) => {
    setErr(null);
    try { await setLeadMentor(memberId, value || null); onChanged(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not change the lead mentor.'); }
  };

  const handleMentorAccess = async (value) => {
    setErr(null);
    try { await setMentorAccess(memberId, value); onChanged(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not change mentor access.'); }
  };

  const fmt = (d) => new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <section className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-reset-brown">Mentoring</h3>
        <p className="text-[11px] text-gray-400 mt-0.5">Team only. {member.name?.split(' ')[0] || 'This member'} never sees this.</p>
      </div>

      {/* Lead mentor */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-reset-brown font-medium" htmlFor="lead-mentor">Lead mentor</label>
        {viewer_is_mentor ? (
          <select
            id="lead-mentor"
            value={member.lead_mentor?.id || ''}
            onChange={e => handleLead(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          >
            <option value="">Not assigned yet</option>
            {mentors.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        ) : (
          <span className="text-sm text-gray-600">{member.lead_mentor?.name || 'Not assigned yet'}</span>
        )}
      </div>

      {/* Mentoring Strategy */}
      {viewer_is_mentor ? (
        <div>
          <label className="block text-sm text-reset-brown font-medium mb-1" htmlFor="mentoring-strategy">Mentoring Strategy</label>
          <p className="text-[11px] text-gray-400 mb-2">Our strategies and observations: how we assist, reach in and work with them, including any referral to a professional. Not clinical notes.</p>
          <textarea
            id="mentoring-strategy"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={8}
            placeholder="What we've noticed, what we're trying, who's reaching in and when…"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
            <span className="text-[11px] text-gray-400">
              {mentoring_strategy?.updated_at
                ? `Last saved by ${mentoring_strategy.updated_by_name} · ${fmt(mentoring_strategy.updated_at)}`
                : 'Nothing saved yet.'}
              {savedAt && !dirty && ' · Saved ✓'}
            </span>
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {mentoring_strategy?.history?.length > 1 && (
            <div className="mt-3">
              <button onClick={() => setShowHistory(v => !v)} className="text-xs text-reset-terracotta hover:underline">
                {showHistory ? 'Hide earlier versions' : `Earlier versions (${mentoring_strategy.history.length - 1})`}
              </button>
              {showHistory && (
                <div className="mt-2 space-y-3">
                  {mentoring_strategy.history.slice(1).map(h => (
                    <div key={h.id} className="border-l-2 border-reset-creamDark pl-3">
                      <p className="text-[10px] text-gray-400 mb-1">{h.saved_by_name} · {fmt(h.saved_at)}</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{h.body || '(empty)'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-400">The Mentoring Strategy is visible to mentors only.</p>
      )}

      {/* Mentor access: Chief Mentor only, and only for Team365 staff */}
      {viewer_is_chief_mentor && member.role === 'admin' && (
        <label className="flex items-center gap-2 text-sm text-reset-brown pt-3 border-t border-gray-50">
          <input
            type="checkbox"
            checked={!!member.is_mentor}
            onChange={e => handleMentorAccess(e.target.checked)}
            className="accent-reset-terracotta"
          />
          {member.name?.split(' ')[0] || 'This person'} is a mentor (can see and write Mentoring Strategies)
        </label>
      )}

      {err && <p className="text-xs text-reset-terracotta">{err}</p>}
    </section>
  );
}

// Jenn, 2026-10-13: Patti and Trish see the Sales & Marketing Control Room
// with their admin. Only someone with affiliate access (Jenn) sees this switch,
// and only on Team365 people. It never opens the Affiliate or Money pages.
function SalesMarketingAccessCard({ member }) {
  const { user } = useAuth();
  const [on, setOn] = useState(!!member.can_see_sales_marketing);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  if (!user?.can_manage_affiliates || member.role !== 'admin' || member.can_manage_affiliates) return null;
  const toggle = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await setSalesMarketingAccess(member.id, !on);
      setOn(!!r.can_see_sales_marketing);
      setMsg(r.can_see_sales_marketing ? 'Done. They see it the next time they open Team365.' : 'Done. Sales & Marketing is hidden for them now.');
    } catch { setMsg('That didn\'t save. Please try again.'); }
    finally { setBusy(false); }
  };
  return (
    <section className="bg-white rounded-2xl shadow-sm p-5">
      <h3 className="text-sm font-semibold text-reset-brown mb-1">Sales &amp; Marketing access</h3>
      <p className="text-xs text-gray-500 mb-3">Lets this Team365 person open the Sales &amp; Marketing Control Room. It never opens Affiliates or Money, and your personal Facebook numbers stay yours.</p>
      <label className="flex items-center gap-2 text-sm text-reset-brown cursor-pointer">
        <input type="checkbox" checked={on} disabled={busy} onChange={toggle} />
        Can see Sales &amp; Marketing
      </label>
      {msg && <p className="text-xs text-gray-500 mt-2">{msg}</p>}
    </section>
  );
}

// ─── Member page left column (Jenn, 2026-10-28) ───────────────────────────────
// Who they are: contact, where they came from, personal, tags, and what came
// across from the old system (migration 195). Their local time uses the time
// zone from the old system or the one they chose.
const tzLabel = (tz) => (tz ? tz.split('/').pop().replace(/_/g, ' ') : null);
const localTimeIn = (tz) => {
  try { return new Date().toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).toLowerCase().replace(/ ([a-z]+)$/, (m, z) => ` ${z.toUpperCase()}`); }
  catch { return null; }
};

function InfoRow({ label, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-2 py-1.5 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-reset-brown break-words">{children}</span>
    </div>
  );
}

function ProfileCard({ title, children }) {
  return (
    <section className="bg-white rounded-2xl shadow-sm p-5">
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h2>
      {children}
    </section>
  );
}

function ProfileColumn({ member, profile, memberId, onTagsChanged }) {
  const p = profile || {};
  const old = p.old_system || {};
  const [newTag, setNewTag] = useState('');
  const [tagErr, setTagErr] = useState(null);
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 60000); return () => clearInterval(t); }, []);

  const addTag = async (e) => {
    e.preventDefault();
    if (!newTag.trim()) return;
    setTagErr(null);
    try { const r = await api.post(`/admin/members/${memberId}/tags`, { tag: newTag.trim() }); onTagsChanged(r.data.tags); setNewTag(''); }
    catch (err) { setTagErr(err.response?.data?.error || 'Could not add the tag.'); }
  };
  const removeTag = async (tag) => {
    if (!window.confirm(`Remove the tag "${tag}"?`)) return;
    try { const r = await api.delete(`/admin/members/${memberId}/tags/${encodeURIComponent(tag)}`); onTagsChanged(r.data.tags); }
    catch { setTagErr('Could not remove the tag.'); }
  };

  const address = [p.street_address, p.city, p.region, p.postal_code].filter(Boolean).join(', ');
  const hasOld = old.source || old.products || old.offers || old.last_note || old.last_appointment || old.created_at;
  const birthday = p.date_of_birth ? new Date(`${p.date_of_birth}T12:00:00`).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' }) : null;

  return (
    <>
      <ProfileCard title="Contact">
        <InfoRow label="Email"><a href={`mailto:${member.email}`} className="hover:underline">{member.email}</a></InfoRow>
        <InfoRow label="Phone">{member.phone ? <a href={`tel:${member.phone}`} className="hover:underline">{member.phone}</a> : <span className="text-gray-400">none on file</span>}</InfoRow>
        <InfoRow label="Other emails">{p.additional_emails}</InfoRow>
        <InfoRow label="Other phones">{p.additional_phones}</InfoRow>
        <InfoRow label="Address">{address || null}</InfoRow>
        <InfoRow label="Country">{p.country}</InfoRow>
        <InfoRow label="Business">{p.business_name}</InfoRow>
        <InfoRow label="Time zone">
          {p.timezone
            ? <>{tzLabel(p.timezone)}<span className="text-gray-400">{p.timezone_source === 'old_system' ? ' · from old system' : ''}</span><br /><span className="text-gray-500">Their time now: {localTimeIn(p.timezone)}</span></>
            : <span className="text-gray-400">not known</span>}
        </InfoRow>
      </ProfileCard>

      <ProfileCard title="Tags">
        <div className="flex flex-wrap gap-1.5">
          {(p.tags || []).length === 0 && <span className="text-sm text-gray-400">No tags yet.</span>}
          {(p.tags || []).map(t => (
            <span key={t.tag} className="inline-flex items-center gap-1 text-xs bg-reset-cream text-reset-brown rounded-full pl-2.5 pr-1.5 py-1" title={t.source === 'old_system' ? 'From the old system' : 'Added in Team365'}>
              {t.tag}
              <button onClick={() => removeTag(t.tag)} aria-label={`Remove tag ${t.tag}`} className="text-gray-400 hover:text-reset-terracotta leading-none">×</button>
            </span>
          ))}
        </div>
        <form onSubmit={addTag} className="flex gap-2 mt-3">
          <input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Add a tag" className="flex-1 min-w-0 text-sm border border-gray-200 rounded-lg px-3 py-1.5" />
          <button type="submit" className="text-xs font-medium bg-reset-terracotta text-white px-3 py-1.5 rounded-lg">Add</button>
        </form>
        {tagErr && <p className="text-xs text-reset-terracotta mt-2">{tagErr}</p>}
      </ProfileCard>

      <ProfileCard title="Where they came from">
        <InfoRow label="First source">{old.source}</InfoRow>
        <InfoRow label="How heard">{member.how_heard}</InfoRow>
        <InfoRow label="Referred by">{member.referred_by?.name || member.referred_by_name}</InfoRow>
        <InfoRow label="Campaign">{member.utm_campaign}</InfoRow>
        {!old.source && !member.how_heard && !member.referred_by && !member.referred_by_name && !member.utm_campaign &&
          <p className="text-sm text-gray-400">Nothing recorded.</p>}
      </ProfileCard>

      {birthday && (
        <ProfileCard title="Personal">
          <InfoRow label="Birthday">{birthday}</InfoRow>
        </ProfileCard>
      )}

      {hasOld && (
        <ProfileCard title="From the old system">
          <InfoRow label="Joined there">{old.created_at ? new Date(old.created_at).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' }) : null}</InfoRow>
          <InfoRow label="Products">{old.products}</InfoRow>
          <InfoRow label="Offers">{old.offers}</InfoRow>
          <InfoRow label="Last appt.">{old.last_appointment}</InfoRow>
          <InfoRow label="Last note">{old.last_note}</InfoRow>
        </ProfileCard>
      )}
    </>
  );
}
